import { useState, type FormEvent } from "react";
import { Send, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Link } from "../../lib/router";

interface Turn {
  from: "visitor" | "helper";
  text: string;
  signup?: boolean;
}

/**
 * "Ask about NutriCart": the AI helper answers visitors before they sign up — what it does, what it costs. It says it
 * is an AI; nothing is stored, and the page sends only the last few turns.
 */
export function AskHelper() {
  const { t, lang } = useI18n();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    const next: Turn[] = [...turns, { from: "visitor", text: q }];
    setTurns(next);
    setText("");
    setBusy(true);
    setFailed(false);
    try {
      const r = await api.post<{ answer: string; signup: boolean }>("/api/help/ask", {
        lang,
        turns: next.slice(-8).map(({ from, text }) => ({ from, text: text.slice(0, 600) })),
      });
      setTurns([...next, { from: "helper", text: r.answer, signup: r.signup }]);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(text);
  };
  // Long conversations stop here: by then trying the app answers more than words.
  const full = turns.length >= 8;
  const chips = [t("landing.ask.q1"), t("landing.ask.q2"), t("landing.ask.q3")];

  return (
    <section className="landing-ask" aria-labelledby="landing-ask-h">
      <h2 id="landing-ask-h">{t("landing.ask.title")}</h2>
      <p className="small muted">
        <Sparkles size={14} aria-hidden="true" /> {t("landing.ask.note")}
      </p>
      {turns.length === 0 && (
        <div className="landing-ask-chips">
          {chips.map((c) => (
            <button key={c} type="button" className="btn small secondary" onClick={() => ask(c)} disabled={busy}>
              {c}
            </button>
          ))}
        </div>
      )}
      {turns.length > 0 && (
        <div className="support-thread" aria-live="polite">
          {turns.map((x, i) => (
            <div key={i} className={`support-msg ${x.from === "visitor" ? "me" : "team ai"}`}>
              {x.from === "helper" && (
                <span className="small muted support-who">
                  <Sparkles size={14} aria-hidden="true" /> {t("support.ai")}
                </span>
              )}
              <p>{x.text}</p>
              {x.signup && (
                <Link to="/register" className="btn small support-link">
                  {t("landing.signup")}
                </Link>
              )}
            </div>
          ))}
          {busy && <p className="small muted">{t("landing.ask.thinking")}</p>}
        </div>
      )}
      {failed && <p className="small muted">{t("landing.ask.failed")}</p>}
      {!full ? (
        <form className="landing-ask-form" onSubmit={submit}>
          <label htmlFor="landing-ask-input" className="visually-hidden">
            {t("landing.ask.title")}
          </label>
          <input
            id="landing-ask-input"
            className="input"
            value={text}
            maxLength={600}
            autoComplete="off"
            enterKeyHint="send"
            placeholder={t("landing.ask.placeholder")}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="btn" disabled={busy || !text.trim()} aria-label={t("support.send")}>
            <Send size={18} />
          </button>
        </form>
      ) : (
        <Link to="/register" className="btn">
          {t("landing.signup")}
        </Link>
      )}
    </section>
  );
}
